import './itemCounter.scss'
import templateUrl from './itemCounter.html'

/**
 * Item Counter component.
 *
 * There are two possible usages:
 * 1. Pass an array to `items`, whose length is the count.
 * 2. Pass an expression to `count`, such as a literal or a function that returns the count.
 *
 * @example <caption>Box counter with an array to items</caption>
 * <tmr-item-counter
 *   items="$ctrl.itemsArray"
 *   label="ITEMCOUNTER.EXPECTED"
 *   max="42"
 * ></tmr-item-counter>
 *
 * @example <caption>Inline counter with a count expression</caption>
 * <tmr-item-counter
 *   type="inline"
 *   label="ITEMCOUNTER.DETECTED"
 *   count="$ctrl.expectedCount"
 * ></tmr-item-counter>
 *
 * @type {ngComponent}
 * @prop {'box'|'inline'} [type=box]
 * @prop {number|array|object=} items
 * @prop {string} label
 * @prop {number=} max
 * @prop {string} [color='accent-700']
 * @prop {string} [countColor='accent-700'] Color of the counters text
 * @prop {function=} count
 */
export const tmrItemCounter = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrItemCounterController',
  bindings: {
    type: '@?',
    items: '<',
    label: '@',
    max: '<',
    color: '@?',
    countColor: '@?',
    count: '&',
  },
}

/**
 * Controller of component {@link tmrItemCounter}.
 *
 * @type {ngController}
 */
export class tmrItemCounterController {
  static ngType = 'controller'

  $onInit() {
    this.type = this.type ?? 'box'
    this.color = this.color ?? 'accent-700'
    this.countColor = this.countColor ?? 'accent-700'
    if (this.count() === undefined) this.count = this.defaultCount
  }

  /** Counts items. Supports `bindings.items` as number, array or object */
  defaultCount({ items = 0 }) {
    if (typeof items === 'number') return items
    if (Array.isArray(items)) return items.length
    return Object.values(items).length
  }

  /**
   * Returns the width of the count bar in percentage
   */
  width() {
    let count = this.count({ items: this.items })
    let percentage = this.max
      ? Math.round((count * 100) / this.max)
      : count
      ? 100
      : 0
    if (percentage > 100) percentage = 100
    return `${percentage}%`
  }

  inlineLabelColor() {
    return this.count({ items: this.items }) ? this.color : 'accent-400'
  }
}
